import { z } from 'zod'

import { findSegmentCollisions } from './segment-dedup'
import type { AdapterSchemaRelation } from '../../../core/engine/contracts'
import { getHostRuntime } from '../../../core/engine/host-runtime'
import {
  checkRateLimit,
  recordCall,
} from '@/Atomic/Widgets/AiChatBot/core/engine/pipeline/write/rate-limiter'
import { clearToolCache } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-cache'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import {
  buildFilterLabelMaps,
  buildRelationReferenceLabels,
  clearCachedFilter,
  getCachedFilter,
  savedFilterStructureSchema,
} from '@/Atomic/Widgets/AiChatBot/core/tools/filter-normalizer'
import type { FilterPreviewAction } from '@/Atomic/Widgets/AiChatBot/core/tools/primitives/filter/show_filter_preview'
import { requireDataClassName } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'
import { DataClassName } from '@/Data/const'

/**
 * Dedicated create-a-segment tool — intentionally SEPARATE from the generic
 * create_entity + save_filter_to_segment path so the segment flow can be tuned
 * without touching (or being broken by) the low-level write tools.
 *
 * It fixes, by construction, the three defects the generic path produced:
 *   1. `entityType` is always set (Company/Person). A dynamic List without it is
 *      not searchable, so PISA rejects the filter with 422 "not searchable".
 *      The generic create left `entityType` to the model, which dropped it because
 *      PISA marks it optional.
 *   2. `type` is forced to `dynamic` and `responsible` to the acting user — no
 *      raw-id owner row in the card (the card shows only the segment name + filter).
 *   3. ONE confirmation card (segment details + filter) instead of a filter
 *      preview followed by a separate create confirmation.
 *
 * The filter is attached in the SAME create POST (`query.filters`) rather than a
 * follow-up PATCH, so the segment is never briefly a match-all list. `query` is an
 * untyped passthrough on the List DataClass, exactly as save_filter_to_segment
 * writes it via update().
 */
export const createSegmentTool = defineTool({
  name: 'create_segment',
  description:
    'Create a new dynamic SEGMENT (a saved List of Company or Person records defined by a filter) in ONE step. ' +
    'Call this ONLY after the user has given a name AND the filter has been built with build_filter (and find_matching_segment has checked for a duplicate). ' +
    'Pass the base entityType (Company or Person), the segment name, the exact structure from build_filter, its matchCount, and the description. ' +
    'When the user asks to save the filter ALREADY APPLIED on the current list page as a new segment ("save this filter", "the applied filter", "the filter on this page"), set `useAppliedFilter: true` and omit `structure` — the tool then uses ONLY the filter applied on the page and ignores any filter built earlier in the chat. The applied filter is what the user sees on the page, never a filter from the conversation. ' +
    'It shows a single confirmation card (segment details + filter) and, on confirm, creates the segment with the filter already attached. ' +
    'Do NOT use create_entity, show_filter_preview, or save_filter_to_segment for a NEW segment — this tool replaces all three for creation. ' +
    'Before creating, it checks for a colliding segment on EVERY path (including useAppliedFilter, where you have no structure to pre-check). If one exists it returns { duplicate: { match, nameMatch } } WITHOUT creating — tell the user which segment already exists (by title/link) and ask whether to open it or create anyway; if they choose to create anyway, call create_segment again with confirmDuplicate: true. ' +
    'Returns { created: true, segmentId, title, url } on success, { duplicate: {...} } when a colliding segment exists, { refine: true } when the user wants to adjust the filter (call build_filter again, then create_segment again), or { cancelled: true }.',
  concurrencySafe: false,
  cacheable: false,
  inputSchema: () =>
    z.object({
      entityType: z
        .string()
        .describe('Base entity the segment holds: Company or Person'),
      name: z.string().describe('The segment name/title the user provided'),
      structure: z
        .preprocess(
          (v) => (typeof v === 'string' ? JSON.parse(v) : v),
          savedFilterStructureSchema,
        )
        .optional()
        .describe(
          'OPTIONAL. The exact SavedFilterStructure from build_filter. Omit it to use the filter already applied on the current list page ("save this filter to a new segment") — the tool reads that from the page itself.',
        ),
      useAppliedFilter: z
        .boolean()
        .optional()
        .describe(
          'Set TRUE when the user means the filter ALREADY APPLIED on the list page ("save this filter", "the applied filter", "the filter on this page"). The tool then uses ONLY the applied page filter and ignores any filter built earlier in the chat; if nothing is applied it returns an error rather than falling back. Leave unset for the build_filter → confirm → create flow.',
        ),
      confirmDuplicate: z
        .boolean()
        .optional()
        .describe(
          'Set TRUE only after create_segment returned { duplicate } and the user explicitly chose to create it anyway. Skips the duplicate guard so the segment is created despite an existing collision. Leave unset on the first attempt.',
        ),
      matchCount: z
        .number()
        .nullable()
        .optional()
        .describe('Match count from build_filter, or null when unavailable'),
      description: z
        .string()
        .describe('Human-readable description of the filter'),
    }),

  async run(args, ctx, toolCallId) {
    const { data } = getHostRuntime()
    const { description } = args

    // A segment holds contacts — only Company or Person are valid bases. Resolve to a
    // DataClassName so the value carries the enum type through to the dedup helper.
    const entityType = requireDataClassName(args.entityType)
    if (
      entityType !== DataClassName.COMPANY &&
      entityType !== DataClassName.PERSON
    ) {
      return {
        error: `A segment must hold Company or Person records, not "${args.entityType}".`,
      }
    }

    // Resolve the filter to save.
    //
    // Priority rules (highest to lowest):
    //
    // A. useAppliedFilter=true (explicit page-save intent, CRM-1450): page filter only.
    //    Error if nothing is applied — never silently fall back to the build-cache.
    //
    // B. args.structure provided (normal build_filter→confirm→create flow): cache wins
    //    over the model-relayed arg because the model re-types the nested structure from
    //    memory across turn boundaries and corrupts it (scalar-vs-array, hallucinated
    //    enum codes). Fall through to the applied page filter, then the arg itself.
    //
    // C. No structure arg, no explicit useAppliedFilter (e.g. model omitted structure
    //    when the user said "save this filter"): page filter wins over the build-cache.
    //    The user is gesturing at what they see on screen — the cache may hold a
    //    completely unrelated filter from an earlier turn. Never pre-empt by saying
    //    "I have no filter"; let the tool determine it from the page.
    const appliedFilter = await data.getAppliedFilterStructure()
    const structure = args.useAppliedFilter
      ? appliedFilter // A — page only
      : args.structure !== undefined
        ? (getCachedFilter(entityType) ?? appliedFilter ?? args.structure) // B — cache guards relay corruption
        : (appliedFilter ?? getCachedFilter(entityType)) // C — page wins over stale cache
    if (!structure) {
      return {
        error: args.useAppliedFilter
          ? 'No filter is applied on this page to save. Apply a filter on the list first, then try again.'
          : 'No filter to build the segment from. Build one with build_filter, or apply a filter on the list page first, then try again.',
      }
    }

    const name = args.name.trim()
    if (!name) {
      return {
        kind: 'text' as const,
        content:
          'Ask the user what to name the segment, then call create_segment again with the name.',
      }
    }

    if (!toolCallId) {
      return { error: 'create_segment requires a toolCallId' }
    }

    // Duplicate guard at the write boundary — runs on EVERY create path (applied
    // filter, build cache, relayed arg), because the resolved `structure` only exists
    // here. find_matching_segment can't cover the useAppliedFilter path (the model has
    // no structure to pre-check with), so the collision check lives where the filter is
    // known. On a hit, return WITHOUT creating so the model can ask; the user overrides
    // with confirmDuplicate: true.
    if (!args.confirmDuplicate) {
      const duplicate = await findSegmentCollisions(entityType, structure, name)
      if (duplicate.match || duplicate.nameMatch) {
        return { duplicate }
      }
    }

    const rateCheck = checkRateLimit('creates')
    if (!rateCheck.allowed) {
      return { kind: 'text' as const, content: rateCheck.message ?? '' }
    }

    // Labels for the confirmation card come from the schema, never from the model
    // (relaying a { code → label } map between calls drops entries). Same source as
    // show_filter_preview so the rendered filter is identical.
    let fieldLabels: Record<string, string> = {}
    let enumLabels: Record<string, Record<string, string>> = {}
    try {
      const { schema, schemaAttributes } = await data.getSchema(
        data.getSchemaName(entityType),
      )
      const maps = buildFilterLabelMaps(schemaAttributes, structure.or)
      fieldLabels = maps.fieldLabels
      enumLabels = maps.enumLabels
      const rawRelations = schema?.get('relations')
      // schema.get() is typed `unknown`; the relations array shape is guaranteed by
      // the schema contract. Guarded by Array.isArray above.
      const relations = Array.isArray(rawRelations)
        ? (rawRelations as AdapterSchemaRelation[])
        : []
      const relMaps = await buildRelationReferenceLabels(
        schemaAttributes,
        relations,
        structure.or,
      )
      fieldLabels = { ...fieldLabels, ...relMaps.fieldLabels }
      enumLabels = { ...enumLabels, ...relMaps.valueLabels }
    } catch {
      // Schema unavailable — the card falls back to raw field keys and codes.
    }

    const action = await new Promise<FilterPreviewAction>((resolve, reject) => {
      ctx.registerToolCallResolver?.(
        toolCallId,
        {
          type: 'filterPreview',
          toolCallId,
          entityType,
          structure,
          matchCount: args.matchCount ?? null,
          description,
          fieldLabels,
          enumLabels,
          primaryAction: 'save',
          primaryLabel: 'components.aiChatBot.filterPreview.createSegment',
          segment: { name },
        },
        // The resolver contract types the card result as `unknown`; the card only
        // ever resolves with a FilterPreviewAction discriminant.
        (result: unknown) => resolve(result as FilterPreviewAction),
        reject,
      )
    })

    if (action.action === 'refine') {
      return { refine: true, previousStructure: structure, entityType }
    }
    if (action.action !== 'save') {
      return { cancelled: true }
    }

    // Everything a searchable dynamic segment needs, in one POST. `entityType` is
    // the fix for the 422; `query.filters` is the single-call attach.
    const attrs: Record<string, unknown> = {
      title: name,
      type: 'dynamic',
      entityType,
      query: { filters: structure },
    }

    // Prefill `responsible` from the backend default (POST /default) — the exact same
    // value and mechanism the manual create drawer uses. This is a DEFAULT, not a
    // lock: `responsible` stays editable on the segment afterward, so the user can
    // reassign the owner. The returned id is the acting user's Contact id (it resolves
    // in the picker; an earlier "empty owner" was just an empty displayTitle on that
    // contact record, a data gap — not an id-space mismatch).
    try {
      const defaults = await data.getDefaultValues(DataClassName.LIST)
      if (typeof defaults.responsible === 'string' && defaults.responsible) {
        attrs.responsible = defaults.responsible
      }
    } catch {
      // Defaults are an enhancement; create the segment without a prefilled owner if
      // the endpoint fails (the user can still assign one on the segment).
    }

    try {
      const created = (await data.create(DataClassName.LIST, attrs)) as {
        id: () => string
      }
      clearToolCache()
      // The create flow is done — drop the cached filter so a later unrelated
      // create can never read this stale structure. Deliberately only on SUCCESS:
      // if create() throws (network/PISA), the cache stays valid so a retry reuses
      // build_filter's exact structure. This can't leak into an unrelated segment —
      // the enforced flow always runs build_filter first, and cacheBuiltFilter
      // overwrites the entry, so a stale value never survives to be read.
      clearCachedFilter(entityType)
      // Recompute membership so the segment's contact list reflects the filter
      // without a page reload — same as the manual SegmentFilterModal.
      data.invalidate(DataClassName.LIST)
      recordCall('creates')
      const segmentId = created.id()
      const nav = getHostRuntime().navigation
      const url = await nav.urlFor(created)
      // Take the user to the new segment. Safe unconditionally here: the filter is
      // attached in the same create POST, so a successful create always has its
      // filter (no risk of stranding on a match-all list).
      if (url) {
        nav.navigate({ url })
      }
      return {
        created: true,
        segmentId,
        title: name,
        ...(url ? { url } : {}),
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      return { error: `Failed to create segment: ${message}` }
    }
  },
})
